// Web Application Class
// --------------------
// Mirrors the back end or graph call
// Supposed to be a simple storage container
//

// Class imports
import {
  convertFromGraphAPItoUI,
  convertListFromGraphAPItoUI,
  getGraphData,
} from "@/utils/transform";
import { deriveAppAbbrev, deriveAppColor } from "@/utils/appIdentity";
import { API, graphqlOperation as _graphqlOperation } from "aws-amplify";
import { assertNoTenantIdInVariables } from "@/services/graphql/assertNoTenantId";

import {
  EMPTY_STRING,
  formatVulnCategory,
  isTestLive,
  LIVE_TESTS_PHASE_VALUE,
  OPTIMIZTIC_TEST_ID_PREFIX,
  TEST_FREQUENCY_FORTNIGHTLY,
  TEST_FREQUENCY_MONTHLY,
  TEST_FREQUENCY_WEEKLY,
  TEST_STATUS_PROVISIONING,
  TEST_STATUS_RUNNING,
  TEST_STATUS_STOPPING,
  TERMINAL_TEST_STATUSES,
} from "../constants/definitions";
import { getIsWebApplicationTestRunning } from "../services/graphql/queries";
import {
  deriveLiveTestState,
  phaseOf,
  TestActivity,
} from "../utils/liveTestState";

import {
  addTestAccount,
  changeWebAppOwner,
  createWebApp,
  deleteOldTestAccounts,
  deleteWebapp,
  fetchTestStatusSnapshot,
  getReducedListOfTestResults,
  scheduleTest,
  startWebappTest, StartTestResult,
  stopWebappTest, StopTestResult,
  updateAppProperties,
  updateAppSettings,
  updateWebApplicationDomains,
  updateTestAccount,
} from "../services/dataListOperations.service";
import {
  eventBus,
  NEW_TEST,
  TEST_STATUS_UPDATE,
  WEBAPP_TEST_STATUS_UPDATE,
  VULN_STATUS_UPDATE,
  WEBAPP_UPDATE,
} from "../services/eventBus.service";
import {
  subscribeToTestStatusChanges,
  subscribeToWebAppTestStatusChanges,
  subscribeToVulnStatusChanges,
  subscribeToWebApplicationChanges,
} from "../services/graphql/customSubscriptions";
import TestResultsClass from "./testResult";
import TestResultOperationsClass from "./testResultOperations";
import TestUserClass from "./testUser";
import WebAppTestStatus from "./webAppTestStatus";
import WebAppVulnHistoryClass from "./webAppVulnHistory";

// Force Cognito User Pools auth on every GraphQL call.
const graphqlOperation = (query: any, variables?: any) => {
  // Reject any client-side tenantId leak before the request leaves
  // the wrapper. See src/services/graphql/assertNoTenantId.ts for
  // the contract — symmetric to the BE rule "never accept tenantId
  // from client input."
  assertNoTenantIdInVariables(variables);
  return {
    ..._graphqlOperation(query, variables),
    authMode: "AMAZON_COGNITO_USER_POOLS" as const,
  };
};

// Cadence for the in-flight test-status resync — see
// startInflightResyncWatchers. 30s strikes the balance: a missed
// Provisioning → Running event will be picked up within ~30s
// instead of staying stuck indefinitely, but cheap enough that an
// idle webapp page (no test in flight) makes zero network calls
// and an active page makes ~4 calls/minute (a status snapshot and a
// slim findings snapshot per tick, both projections without
// screenshots). Bump higher if telemetry shows the poll causing
// load; lower if customers report stale windows longer than 30s.
const INFLIGHT_RESYNC_INTERVAL_MS = 30_000;

// Lifecycle order for a test's status. Used by the resync path to
// guard against a stale snapshot regressing local state — the
// subscription is the real-time authority and may already have
// delivered a forward status (e.g. Running) before the resync's
// snapshot is computed on the read side. If the snapshot lands
// with a lower-ranked status, we drop it rather than rewind.
//
// Subscriptions stay free to apply any update — they're real-time
// pushes from the write path. Only the periodic snapshot reader is
// guarded here, because it's the only path susceptible to read-
// side eventual-consistency lag.
function testStatusRank(status?: string | null): number {
  if (!status) return 0;
  if (status === TEST_STATUS_PROVISIONING) return 1;
  if (status === TEST_STATUS_RUNNING) return 2;
  if (status === TEST_STATUS_STOPPING) return 3;
  if (TERMINAL_TEST_STATUSES.has(status)) return 4;
  return 0;
}

// Which code path is writing the live-run signal. Carried on every
// setTestActivity() call so the breadcrumb it logs names the writer — the
// question that took a morning of backend archaeology to NOT answer the
// first time this bug appeared.
export const LIVE_SIGNAL_SOURCE = {
  startClick: "startClick",
  stopClick: "stopClick",
  startTestOptimiztic: "startTest:optimiztic",
  startTestAssignedId: "startTest:assignedId",
  startTestRejected: "startTest:rejected",
  stopTestOptimiztic: "stopTest:optimiztic",
  stopRejected: "stopRejected",
  stopAlreadyEnded: "stopAlreadyEnded",
  statusEvent: "statusEvent",
  statusEventTerminal: "statusEvent:terminal",
  isTestRunningCoarse: "isTestRunning:coarse",
  isTestRunningEnded: "isTestRunning:ended",
  dashboardPreload: "dashboardPreload",
  unspecified: "unspecified",
} as const;

export type LiveSignalSource =
  (typeof LIVE_SIGNAL_SOURCE)[keyof typeof LIVE_SIGNAL_SOURCE];

export interface SetTestActivityOptions {
  // Who is writing. Logged, never behavioural.
  source?: LiveSignalSource;
  // The caller is starting a NEW run (a Pentest Now click), so its
  // Provisioning is a forward step rather than a rewind of whatever the
  // previous run left behind. Only the two user-intent writers set this.
  startsNewRun?: boolean;
}

// Are these two live-signal records about the same run?
//
// Deliberately asymmetric: we answer false ONLY on positive evidence of
// difference — two known-but-different test ids. Every other case (either
// id missing, ids equal) answers true. The alternative, guessing "different
// run" from a startTime mismatch, would let a stale phase through as a
// "new run" and rewind the button; the transitional windows where an id is
// missing are exactly where that guess would be least reliable, because the
// optimiztic click path stamps startTime from the CLIENT clock while every
// backend record carries the server's.
//
// The known cost of that choice: if an idless signal (the dashboard
// preload's shape) is left tracking a run whose terminal event we never
// received, and a scheduled run then starts, its Provisioning reads as a
// rewind and is dropped — the button shows Stop Pentest a few minutes
// early. It converges on the next event (Running ranks equal-or-higher, so
// it lands) and never hides a live run behind a Start button, which is the
// failure direction that actually costs the user something.
function isSameRun(current: TestActivity, incoming: TestActivity): boolean {
  if (current.testId && incoming.testId) {
    return current.testId === incoming.testId;
  }
  return true;
}

interface WebAppDictionary {
  id: string;
  name: string;
  url: string;
  abbrev: string;
  color: string;
  securityHeaderName: string;
  securityHeaderValueSet: boolean;
  render: string;
  owner: string;
  verificationString: string;
  ownershipVerified: boolean;
  testAcknowledged: boolean;
  allowedLinks: any;
  deniedLinks: any;
  apiDomains: any;
  maxRequestsPerSecond: number;
  featureTeamName: string;
  frontEndFramework: string;
  backEndFramework: string;
  dataType: any[];
  userType: any[];
  testFrequency: string;
  dateToTest: number;
  dateAdded: number;
  testAccounts: {
    items: any[];
  };
}

const MAX_TEST_RESULTS_TO_FULLY_LOAD = 1;

class WebApplicationClass {
  id: string;
  name: string;
  url: string;
  abbrev: string;
  color: string;
  securityHeaderName: string;
  securityHeaderValueSet: boolean;
  render: string;
  owner: string;
  verificationString: string;
  ownershipVerified: boolean;
  testAcknowledged: boolean;
  allowedLinks: any;
  deniedLinks: any;
  // Additional in-scope API domains. Same shape as deniedLinks: a
  // comma-joined string in the UI, a list of strings over the wire.
  apiDomains: any;
  maxRequestsPerSecond: number;
  featureTeamName: string;
  frontEndFramework: string;
  backEndFramework: string;
  dataType: any[];
  userType: any[];
  testAccounts: TestUserClass[];
  testAccountsNextToken: string;
  tests: TestResultsClass[];
  testsNextToken: string;
  // -1 = not yet fetched (shallow load), 0+ = actual count
  testCount: number;
  dateToTest: number;
  testFrequency: string;
  dateAdded: number;
  webAppTestStatus: WebAppTestStatus;
  vulnHistory: WebAppVulnHistoryClass;
  // The SINGLE source of truth for "is a run in flight on this webapp
  // right now, and in what phase?". Null when no run is live. Written
  // ONLY through setTestActivity() (which refuses terminal statuses) and
  // read ONLY through deriveLiveTestState()/isWebAppTestRunning() in
  // utils/liveTestState.ts. Replaces the former trio of signals
  // (testRunning boolean + inFlightTestStatus + inFlightStartTime), which
  // were written by four separate code paths keyed off different fields
  // and could disagree. See utils/liveTestState.ts for the full contract.
  testActivity: TestActivity | null;
  readyToDisplay: boolean;
  lastStartButtonClickTime: Date;

  // Live-update subscription handles (custom AppSync subscriptions, one per
  // event type, all filtered server-side by webappid). Stored so they can be
  // unsubscribed cleanly on logout / app teardown. Null when not subscribed.
  subscriptionToTestStatus: any;
  subscriptionToWebAppTestStatus: any;
  subscriptionToVulnStatus: any;
  subscriptionToWebappUpdates: any;

  // In-flight resync — fallback for the case where the
  // onTestStatusChange subscription silently drops (idle WebSocket,
  // network blip, AppSync flakiness). While a test is in flight on
  // this webapp we re-fetch the test status every
  // INFLIGHT_RESYNC_INTERVAL_MS and also on tab visibility →
  // visible. Without this, a missed Provisioning → Running event
  // leaves the UI pinned to "Starting…" indefinitely. Both handles
  // are stored so they tear down cleanly on
  // unsubscribeFromLiveUpdates.
  private inflightResyncInterval: ReturnType<typeof setInterval> | null = null;
  private inflightResyncVisibilityHandler: (() => void) | null = null;

  // The subscription client the live-update subscriptions were opened on.
  // Retained so the resync path can re-open a discovery stream that died
  // (see resyncInflightFindings) without having to thread the client back
  // down from the component tree.
  private liveUpdatesClient: any = null;
  // Disposer for the reconnect listener registered on that client.
  private liveUpdatesReconnectDisposer: (() => void) | null = null;

  constructor() {
    this.id = EMPTY_STRING;
    this.name = EMPTY_STRING;
    this.url = EMPTY_STRING;
    this.abbrev = EMPTY_STRING;
    // Empty (not "#000000") so the `color || deriveAppColor(name)` fallback
    // used at every render site fires for a freshly created app. A truthy
    // hex sentinel here would defeat that fallback and render a black badge
    // until the app is reloaded from the backend (which never persists color).
    this.color = EMPTY_STRING;
    this.securityHeaderName = EMPTY_STRING;
    this.securityHeaderValueSet = false;
    this.render = EMPTY_STRING;
    this.owner = EMPTY_STRING;
    this.verificationString = EMPTY_STRING;
    this.ownershipVerified = false;
    this.testAcknowledged = false;
    this.allowedLinks = EMPTY_STRING;
    this.deniedLinks = EMPTY_STRING;
    this.apiDomains = EMPTY_STRING;
    this.maxRequestsPerSecond = 0;
    this.featureTeamName = EMPTY_STRING;
    this.frontEndFramework = EMPTY_STRING;
    this.backEndFramework = EMPTY_STRING;
    this.dataType = [];
    this.userType = [];
    this.testAccounts = [];
    this.testAccountsNextToken = EMPTY_STRING;
    this.tests = [];
    this.testsNextToken = EMPTY_STRING;
    this.testCount = -1;
    this.dateToTest = 0;
    this.testFrequency = EMPTY_STRING;
    this.dateAdded = 0;
    this.webAppTestStatus = new WebAppTestStatus();
    // Vuln history
    this.vulnHistory = new WebAppVulnHistoryClass();
    // No run in flight until an authoritative source says otherwise.
    this.testActivity = null;

    // Used for display purposes - to determine if the webapp has loaded
    this.readyToDisplay = false;

    // Start button click time
    this.lastStartButtonClickTime = new Date(0);

    // Live-update subscriptions are wired up after login by
    // subscribeToLiveUpdates() and torn down on logout.
    this.subscriptionToTestStatus = null;
    this.subscriptionToWebAppTestStatus = null;
    this.subscriptionToVulnStatus = null;
    this.subscriptionToWebappUpdates = null;
  }

  // Set Web Application
  // --------------------
  //
  async setWebApplication(
    webappDictionary: WebAppDictionary,
    subscriptionClient: any,
    loadFull: boolean = true
  ): Promise<void> {
    if (webappDictionary) {
      // Test status is populated later in bulk via listWebAppTestStatuses
      // (see App.tsx preloadWebAppTestStatuses). We construct an empty status
      // here rather than firing a per-webapp getWebAppTestStatus call.
      this.webAppTestStatus = new WebAppTestStatus();
      // testActivity is intentionally not set here. Post the
      // FailedTestFindings BE deploy, webAppTestStatus.currentPhase
      // carries terminal phases only and never signals "test in
      // flight", so it cannot seed the live-run signal. That signal
      // flows through the dashboard preload (App.tsx applyTests →
      // setTestActivity) and through testStatus subscriptions — both
      // authoritative. The constructor already initialized testActivity
      // to null; leave it there until the authoritative sources speak.

      // Webapp details
      this.id = webappDictionary.id;
      this.name = webappDictionary.name;
      this.url = webappDictionary.url;
      this.abbrev = webappDictionary.abbrev;
      this.color = webappDictionary.color;
      this.securityHeaderName = webappDictionary.securityHeaderName || EMPTY_STRING;
      this.securityHeaderValueSet = webappDictionary.securityHeaderValueSet || false;
      this.render = webappDictionary.render;
      this.owner = webappDictionary.owner;
      this.verificationString = webappDictionary.verificationString;
      this.ownershipVerified = webappDictionary.ownershipVerified;
      this.testAcknowledged = webappDictionary.testAcknowledged ?? false;
      this.allowedLinks = webappDictionary.allowedLinks;
      this.deniedLinks = webappDictionary.deniedLinks;
      this.apiDomains = webappDictionary.apiDomains;
      this.maxRequestsPerSecond = webappDictionary.maxRequestsPerSecond;
      this.featureTeamName = webappDictionary.featureTeamName;
      this.frontEndFramework = webappDictionary.frontEndFramework;
      this.backEndFramework = webappDictionary.backEndFramework;
      this.dataType = convertListFromGraphAPItoUI(webappDictionary.dataType);
      this.userType = convertListFromGraphAPItoUI(webappDictionary.userType);
      this.dateToTest = webappDictionary.dateToTest;
      this.testFrequency = convertFromGraphAPItoUI(
        webappDictionary.testFrequency
      );
      this.dateAdded = webappDictionary.dateAdded;

      this.readyToDisplay = true;

      // If the ownership has been verified and a full load is requested, fetch tests and history.
      // Dashboard initial loads pass loadFull=false to avoid heavy parallel API calls at login.
      if (this.ownershipVerified && loadFull) {
        // Populate the list of tests and test accounts
        // Test accounts is the full list, but test results we need to retreive from the server
        this.getVulnHistory();
        await this.getTests();
        await this.subscribeToRunningTest(subscriptionClient);
      }
      this.getTestAccounts(webappDictionary.testAccounts.items);
    }
  }

  // Load Full Data
  // ---------------
  // Called when a webapp detail page opens and the webapp was loaded
  // shallowly during dashboard initialisation (loadFull=false). Fetches
  // tests, vuln history, and subscribes to any running test.
  //
  async loadFullData(subscriptionClient: any): Promise<void> {
    if (!this.ownershipVerified) return;
    this.getVulnHistory();
    await this.getTests();
    await this.subscribeToRunningTest(subscriptionClient);
  }

  // Is Test Scheduled
  // -------------------
  //
  isTestScheduled(): boolean {
    if (
      this.testFrequency === TEST_FREQUENCY_WEEKLY ||
      this.testFrequency === TEST_FREQUENCY_FORTNIGHTLY ||
      this.testFrequency === TEST_FREQUENCY_MONTHLY
    ) {
      return true;
    }
    return false;
  }

  /* -------------- */
  /*  List Getters  */
  /* -------------- */

  // Get Vuln History
  // ------------------
  //
  async getVulnHistory(): Promise<void> {
    return this.vulnHistory.getVulnHistory(this.id);
  }

  // Get Tests
  // ----------
  // Get a reduced list of test results in a list
  // This returns the test result without the vulns or pages for each test
  // Note that a web app may have no tests, therefore the test results list is empty
  //
  async getTests(): Promise<void> {
    if (!this.id) return;

    // Reset any pre-populated shallow tests (e.g. from the dashboard's
    // preloadLatestTestsForDashboard batch call) so we don't accumulate
    // duplicates when loadFullData runs.
    this.tests = [];

    try {
      const data = await getReducedListOfTestResults(this.id);

      if (data) {
        const tests = data["items"];

        if (tests && tests.length) {
          this.testCount = tests.length;
          const sorter = new TestResultOperationsClass();
          const sortedTests = sorter.sortByTestDate(tests);
          const maxIterations = Math.min(
            sortedTests.length,
            MAX_TEST_RESULTS_TO_FULLY_LOAD
          );
          for (let index = 0; index < maxIterations; index++) {
            const test = new TestResultsClass();
            await test.getFullTestResult(sortedTests[index].id);
            this.tests.push(test);
          }
        } else {
          this.testCount = 0;
        }
      }
    } catch (error) {
      this.tests = [];
    }
  }

  // Backfill the test history slim — populates this.tests with every
  // test the BE knows about for this webapp, hydrating only the slim
  // metadata (id, dates, status, endTrigger) rather than the full
  // vuln payload. Used by the WebApplication page's TestSelector
  // dropdown so users can pick historical tests by date.
  //
  // Non-destructive: tests already in this.tests (e.g. the latest
  // terminal from the dashboard preload, already fully hydrated) are
  // preserved. Only ids that aren't already present are added as
  // slim stubs. When the user picks a stub via the dropdown, the
  // WebApplication page calls getFullTestResult to hydrate it on
  // demand.
  //
  // Sort: newest first by startTime, so the dropdown renders in the
  // order users expect.
  async backfillTestHistory(): Promise<void> {
    if (!this.id) return;

    try {
      const data = await getReducedListOfTestResults(this.id);
      if (!data) return;

      const incoming = data["items"];
      if (!Array.isArray(incoming) || incoming.length === 0) return;

      const knownIds = new Set(
        this.tests.map((test) => test.id).filter(Boolean),
      );

      for (const reducedDict of incoming) {
        if (!reducedDict?.id || knownIds.has(reducedDict.id)) continue;
        const stub = new TestResultsClass();
        stub.setReducedTestResult(reducedDict);
        this.tests.push(stub);
      }

      // Sort newest first; the dropdown reads this order directly and
      // the rest of the app's "latest test" derivations (e.g. tests[0])
      // assume newest first too.
      this.tests.sort(
        (a, b) => Number(b.startTime ?? 0) - Number(a.startTime ?? 0),
      );
      this.testCount = this.tests.length;
    } catch {
      // Swallow — the dropdown just stays with the single preloaded
      // entry. Non-fatal for the page.
    }
  }

  // Load Test Count
  // ----------------
  // Fetches only the count of test results without loading full test data.
  // Used by list views (e.g. /applications) where only a count is needed and
  // the webapp was loaded shallowly (loadFull=false) during dashboard init.
  //
  async loadTestCount(): Promise<void> {
    if (!this.id || !this.ownershipVerified) return;

    try {
      const data = await getReducedListOfTestResults(this.id);
      this.testCount = data?.items?.length ?? 0;
    } catch {
      this.testCount = 0;
    }
  }

  // Get Test Accounts
  // ------------------
  // Test accounts are provided in entirity when the web application is listed - so no need for a new call
  // This is reflected in the TestUserClass
  //
  getTestAccounts(listOfTestResults: any[]): void {
    for (var i = 0; i < listOfTestResults.length; i++) {
      let testUser = new TestUserClass(listOfTestResults[i]);
      this.testAccounts.push(testUser);
    }
  }

  /* ------------------ */
  /*  Backend Updaters  */
  /* ------------------ */

  // Delete Test Accounts
  // ---------------------
  //
  deleteOldTestAccounts(): void {
    this.testAccounts.map((element) => {
      deleteOldTestAccounts(element.id).then((data) => {
        // Nothing to do with the response
      });
    });
    // Delete the test accounts locally
    this.testAccounts = [];
  }

  // Add Test Account
  // -----------------
  //
  addTestAccount(testUser: any): void {
    // Add it to the front end
    this.testAccounts.push(testUser);
    // Add it to the back end
    addTestAccount(this.id, testUser).then((data) => {
      // Do nothing
    });
  }

  // Save Test Accounts
  // -------------------
  // Diffs the incoming list against the current testAccounts and issues the
  // minimum set of backend operations needed:
  //   - existing account (matched by userPriority) → update in place, preserving the ID
  //   - new account (no matching priority in existing list) → create
  //   - old account not in the new list → delete
  //
  // This replaces the old delete-everything-then-recreate pattern, which had a
  // race condition: fire-and-forget deletes could run after newly-created records,
  // wiping them out and leaving the webapp's foreign-key reference broken.
  //
  async saveTestAccounts(newAccounts: TestUserClass[]): Promise<void> {
    const existingByPriority = new Map<number, TestUserClass>();
    for (const existing of this.testAccounts) {
      existingByPriority.set(existing.userPriority, existing);
    }

    const newPriorities = new Set(newAccounts.map((account) => account.userPriority));

    // Delete accounts that are no longer in the list
    for (const priority of Array.from(existingByPriority.keys())) {
      if (!newPriorities.has(priority)) {
        await deleteOldTestAccounts(existingByPriority.get(priority)!.id);
      }
    }

    // Update existing or create new accounts
    for (const newAccount of newAccounts) {
      const existing = existingByPriority.get(newAccount.userPriority);
      if (existing) {
        await updateTestAccount(existing.id, newAccount);
        newAccount.id = existing.id;
      } else {
        const created = await addTestAccount(this.id, newAccount);
        if (created?.id) {
          newAccount.id = created.id;
        }
      }
    }

    this.testAccounts = newAccounts;
  }

  // Change Web App Owner
  // ---------------------
  //
  // Returns true when the owner was persisted (or was already this user).
  //
  // Awaitable so a caller can report a failure rather than announcing a
  // transfer that never happened. The local value is still applied optimistically
  // for callers that don't await, but it is REVERTED if the write fails, so the
  // in-memory model cannot keep claiming an owner the backend rejected.
  async changeOwner(newOwnerUsername: string): Promise<boolean> {
    if (newOwnerUsername === this.owner) {
      return true;
    }
    const previousOwner = this.owner;
    this.owner = newOwnerUsername;
    try {
      const changed = await changeWebAppOwner(this.id, newOwnerUsername);
      if (!changed) {
        this.owner = previousOwner;
        return false;
      }
      return true;
    } catch (changeOwnerError) {
      this.owner = previousOwner;
      console.error("changeWebAppOwner failed");
      return false;
    }
  }

  // Delete Web App
  // ---------------
  // Dont await this - as the caller needs to know so they can take action
  //
  delete(): void {
    // Delete the web app
    deleteWebapp(this.id).then((data) => {
      // Reset the local values
      if (data) {
        // Reset this
        this.id = EMPTY_STRING;
        this.name = EMPTY_STRING;
        this.url = EMPTY_STRING;
        this.abbrev = EMPTY_STRING;
        // Match the constructor: empty so the render-time deriveAppColor
        // fallback applies rather than a black-badge sentinel.
        this.color = EMPTY_STRING;
        this.securityHeaderName = EMPTY_STRING;
        this.securityHeaderValueSet = false;
        this.render = EMPTY_STRING;
        this.owner = EMPTY_STRING;
        this.verificationString = EMPTY_STRING;
        this.ownershipVerified = false;
        this.allowedLinks = EMPTY_STRING;
        this.deniedLinks = EMPTY_STRING;
        this.apiDomains = EMPTY_STRING;
        this.maxRequestsPerSecond = 0;
        this.featureTeamName = EMPTY_STRING;
        this.frontEndFramework = EMPTY_STRING;
        this.backEndFramework = EMPTY_STRING;
        this.dataType = [];
        this.userType = [];
        this.testAccounts = [];
        this.testAccountsNextToken = EMPTY_STRING;
        this.tests = [];
        this.testsNextToken = EMPTY_STRING;
        this.dateToTest = 0;
        this.testFrequency = EMPTY_STRING;
        this.dateAdded = 0;

        this.readyToDisplay = false;
      }
    });
  }

  // Create Web App
  // ---------------
  //
  // Bundles everything the Add-Application wizard collects into one
  // mutation: webapp metadata, RPS, security header, test accounts,
  // link scope. The BE accepts these inline on CreateWebApplicationInput
  // so the wizard no longer needs the post-create chain of
  // updateAppSettings + saveTestAccounts + setSecurityHeaderForWebapp.
  //
  // testAccounts come from wizard state rather than `this.testAccounts`
  // because, before this call resolves, the webapp record doesn't yet
  // exist server-side and the model's `testAccounts` array isn't
  // populated. securityHeaderValue is wizard-state only too — the
  // model persists `securityHeaderName` + `securityHeaderValueSet`
  // but never the value itself (which lives in SSM).
  async createNewWebApp(
    subscriptionClient: any,
    testAccounts: TestUserClass[] = [],
    securityHeaderValue: string | null = null,
    // Purchased license to bind this app to (null -> BE picks the first free
    // slot, and rejects the create if there isn't one).
    entitlementId: string | null = null,
  ) {
    // Preserve abbrev and color — they're derived on the frontend but not
    // part of CreateWebApplicationInput, so the backend won't return them.
    const savedAbbrev = this.abbrev || deriveAppAbbrev(this.name);
    const savedColor = this.color || deriveAppColor(this.name);

    const data = await createWebApp(
      this.name,
      this.url,
      this.featureTeamName,
      this.frontEndFramework,
      this.backEndFramework,
      this.dataType,
      this.userType,
      this.testAcknowledged,
      this.maxRequestsPerSecond,
      this.securityHeaderName || null,
      securityHeaderValue,
      testAccounts.map((account) => ({
        username: account.username || "",
        password: account.password,
        pin: account.pin,
        totpSecret: account.totpSecret,
        userPriority: account.userPriority,
      })),
      this.allowedLinks,
      this.deniedLinks,
      this.apiDomains,
      entitlementId,
    );
    if (data) {
      await this.setWebApplication(data, subscriptionClient);
      this.abbrev = savedAbbrev;
      this.color = savedColor;
    }
  }

  // Update Web App Properties
  // --------------------------
  // This is the
  async updateAppProperties() {
    // Add it to the back end - no need to add it to the front end, as this is where it came from
    await updateAppProperties(
      this.id,
      this.featureTeamName,
      this.frontEndFramework,
      this.backEndFramework,
      this.dataType,
      this.userType
    );
  }

  // Update Specific Web App Settings
  // -----------------------------------
  // This is the
  updateAppSettings(): void {
    // Add it to the back end - no need to add it to the front end, as this is where it came from
    updateAppSettings(
      this.id,
      this.allowedLinks,
      this.deniedLinks,
      this.maxRequestsPerSecond
    ).then((data) => {
      // Nothing to do, already in the front end
    });
  }

  // Update Domains (pre-verification re-scope)
  // -------------------------------------------
  // Re-sets url + apiDomains on this UNVERIFIED app in place. The backend
  // enforces the security gates and re-canonicalises the input; on success we
  // resync the local model from the returned canonical values. Rejections are
  // thrown (with the backend message) for the caller to surface.
  async updateDomains(url: string, apiDomains: string[]): Promise<void> {
    const updated = await updateWebApplicationDomains(this.id, url, apiDomains);
    if (updated) {
      if (updated.url !== undefined) this.url = updated.url;
      if (updated.apiDomains !== undefined) this.apiDomains = updated.apiDomains;
      if (updated.ownershipVerified !== undefined)
        this.ownershipVerified = updated.ownershipVerified;
      if (updated.verificationString !== undefined)
        this.verificationString = updated.verificationString;
    }
  }

  // Schedule Test
  // --------------
  //
  scheduleTest(): void {
    // Add it to the back end - no need to add it to the front end, as this is where it came from
    scheduleTest(this.id, this.dateToTest, this.testFrequency).then((data) => {
      // Nothing to do, already in the front end
    });
  }

  // Start Test
  // ------------
  // Starts a full test or a retest (retests only re-check previously found vulns).
  // Returns the result so the caller can display error messages to the user.
  async startTest(subscriptionClient: any, retestOnly: boolean = false): Promise<StartTestResult> {
    // Optimiztic local state — applied BEFORE the backend round-trip so
    // counts, vuln table, status indicator and button all flip atomically
    // on click. Per agreed UX, no rollback on backend failure: if the
    // backend rejects, the websocket layer (or the explicit
    // setErrormessage from the caller) will reconcile any divergence.
    // Optimiztically record the in-flight run through the single setter —
    // Provisioning is the initial phase. The placeholder id is swapped in
    // just below (setTestActivity is re-called with the real testid on the
    // success path); until then testId stays null, which is fine — the
    // selector keys on phase, not id.
    this.setTestActivity(
      {
        testId: null,
        testStatus: TEST_STATUS_PROVISIONING,
        startTime: Math.floor(Date.now() / 1000),
      },
      {
        source: LIVE_SIGNAL_SOURCE.startTestOptimiztic,
        // The user asked for a NEW run; its Provisioning is a forward step
        // even if a previous run left a later phase behind.
        startsNewRun: true,
      },
    );
    this.lastStartButtonClickTime = new Date();
    this.webAppTestStatus.currentPhase = LIVE_TESTS_PHASE_VALUE;
    this.webAppTestStatus.allcrits = 0;
    this.webAppTestStatus.allhighs = 0;
    this.webAppTestStatus.allmeds = 0;
    this.webAppTestStatus.alllows = 0;
    this.webAppTestStatus.opencrits = 0;
    this.webAppTestStatus.openhighs = 0;
    this.webAppTestStatus.openmeds = 0;
    this.webAppTestStatus.openlows = 0;
    this.webAppTestStatus.calcRiskScore();
    this.webAppTestStatus.calcPercentComplete();

    // Optimiztic placeholder with a locally-generated id so every FE
    // surface (selector, auto-select, route params) can identify and
    // track this test from the moment it's added — before the BE
    // round-trip resolves. The id is swapped for the real BE testid
    // below; consumers that cared about the old value pick up the
    // swap via the NEW_TEST payload.
    const placeholderId = `${OPTIMIZTIC_TEST_ID_PREFIX}${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 10)}`;
    const placeholder = TestResultsClass.createDefault(placeholderId, this.owner);
    placeholder.testStatus = TEST_STATUS_PROVISIONING;
    this.tests.unshift(placeholder);

    // Synchronous emit so the UI updates on the same frame as the
    // user's click: Pentest button → Stop Pentest, Testing Live badge
    // appears, dropdown shows the Live entry, Findings tiles zero
    // out, vuln table flips to "Test in progress". The BE round-trip
    // happens below; until it resolves the FE shows the optimiztic
    // state and reconciles afterwards.
    eventBus.emit(NEW_TEST);

    const result = await startWebappTest(this.id, retestOnly);

    if (result.testid) {
      // Swap the placeholder's local id for the real BE-assigned one
      // so subsequent onTestStatusChange / getFullTestResult calls
      // (which key on BE id) target the same instance. Re-emit
      // NEW_TEST carrying both ids so listeners can migrate any
      // local state that pinned the placeholder id (e.g.
      // selectedTestId in WebApplication.tsx).
      //
      // No live vuln subscription is wired here: per the simplified
      // UX model the FE doesn't stream vulns during a run. The
      // webapp page refetches the test's full vulns once
      // onTestStatusChange reports the terminal transition.
      const previousId = placeholder.id;
      placeholder.id = result.testid;
      // Attach the real testid to the optimiztic activity so subsequent
      // reconciles/resyncs can correlate it. Still Provisioning until a
      // subscription event advances it — and deliberately NOT flagged as
      // starting a new run, so that if the backend has already reported
      // Running while this mutation was in flight, the monotonic rule keeps
      // the later phase and takes only the testid from this write.
      this.setTestActivity(
        {
          testId: result.testid,
          testStatus: TEST_STATUS_PROVISIONING,
          startTime: this.testActivity?.startTime ?? Math.floor(Date.now() / 1000),
        },
        { source: LIVE_SIGNAL_SOURCE.startTestAssignedId },
      );
      eventBus.emit(NEW_TEST, {
        previousId,
        currentId: result.testid,
      });
    } else {
      // Backend rejected. Drop the placeholder, then reconcile the live
      // signal against actual BE state rather than unilaterally clearing
      // it. The common multi-admin race is: Admin A starts a test, Admin
      // B clicks Pentest before A's test completes, and B's start gets
      // rejected with "test already running" — the activity must STAY set
      // on B's side because A's test really is running. Blanket-clearing
      // would briefly flash "Pentest Now" / drop the Testing Live badge
      // until the next subscription event corrected it.
      //
      // isTestRunning() itself now writes testActivity from the
      // endTime-authoritative BE state (set when a run really exists,
      // cleared otherwise), so there's nothing to assign here. Only one
      // extra round-trip and only on failure — the success path stays
      // snappy.
      const idx = this.tests.indexOf(placeholder);
      if (idx !== -1) this.tests.splice(idx, 1);
      try {
        await this.isTestRunning();
      } catch {
        // If isTestRunning itself fails, fall back to clearing —
        // worst case the next subscription event corrects us.
        this.setTestActivity(null, {
          source: LIVE_SIGNAL_SOURCE.startTestRejected,
        });
      }
      eventBus.emit(NEW_TEST);
    }

    return result;
  }

  // Stop Test
  // ----------
  // Issues the stop mutation and returns the backend's response so the
  // caller can surface a rejection message (the backend rejects stop
  // during Provisioning / Stopping with a descriptive message).
  //
  // Optimiztically advances the live signal to Stopping so the button
  // flips to the disabled "Stopping…" state on the same frame as the
  // click; the subscription drives the subsequent Stopping → terminal
  // transition (which clears the activity). Stopping is deliberately
  // still "in flight" for the FE even though its endTime is set (the
  // per-test environment is still tearing down).
  async stopTest(): Promise<StopTestResult> {
    // Unsubscribe the running test from vuln updates before stopping —
    // no new vulns are expected once the stop is issued.
    const runningTest = this.tests.find((test) => test.isLive);
    if (runningTest) {
      runningTest.unsubscribeFromVulns();
    }

    this.setTestActivity(
      {
        testId: runningTest?.id ?? this.testActivity?.testId ?? null,
        testStatus: TEST_STATUS_STOPPING,
        startTime: this.testActivity?.startTime ?? null,
      },
      { source: LIVE_SIGNAL_SOURCE.stopTestOptimiztic },
    );

    return await stopWebappTest(this.id);
  }

  // Set Test Activity
  // ------------------
  // The ONLY writer of the live-run signal. Every path that learns
  // "a run started / advanced / ended" funnels through here so the four
  // former writers can never diverge again. Refuses to store a terminal
  // (or empty) status — a terminal transition clears the activity to
  // null — so callers may hand it a raw payload without pre-filtering.
  //
  // MONOTONIC within a run. A run's phase only ever moves forwards
  // (Provisioning → Running → Stopping → cleared). A write that would move
  // the SAME run backwards is dropped, because a backwards phase is always
  // either stale or invented, and the two outcomes are not symmetric: a
  // slightly-stale forward phase costs the user nothing, whereas rewinding
  // Running → Provisioning replaces the Stop Pentest button with a disabled
  // "Starting…" and strands a live pentest the user can no longer stop.
  // That is the failure seen on Juice Shop on 2026-08-16: the backend had
  // said Running since 03:26:04 and went on saying it for two and a half
  // hours, while the page sat on "Starting…" until the user logged out and
  // back in.
  //
  // Two escapes from the rule, both narrow:
  //   * options.startsNewRun — the user just clicked Pentest Now. Their
  //     intent starts a NEW run, so Provisioning is a forward step, not a
  //     rewind, whatever the previous run's signal happened to say.
  //   * a placeholder phase (see TestActivity.phaseIsPlaceholder) — a value
  //     we invented rather than one the backend reported. Replacing it with
  //     the real phase is a correction, so anything may overwrite it.
  //
  // Clearing (activity null / terminal) is never gated: "the run ended" is
  // always the newest possible truth about a run.
  setTestActivity(
    activity: TestActivity | null,
    options: SetTestActivityOptions = {},
  ): void {
    const source = options.source ?? LIVE_SIGNAL_SOURCE.unspecified;
    const previous = this.testActivity;

    if (
      !activity ||
      !activity.testStatus ||
      TERMINAL_TEST_STATUSES.has(activity.testStatus)
    ) {
      this.testActivity = null;
      this.logLiveSignalWrite(source, previous, null, "cleared");
      return;
    }

    if (
      previous &&
      !previous.phaseIsPlaceholder &&
      !options.startsNewRun &&
      isSameRun(previous, activity) &&
      testStatusRank(activity.testStatus) < testStatusRank(previous.testStatus)
    ) {
      // Rewind refused. The incoming record can still carry identity we
      // lack (the dashboard preload ships a phase with no testid; the
      // optimiztic click window has no testid either), and keeping that is
      // what lets the resync find this run again — so adopt the detail
      // while discarding the phase.
      if (!previous.testId && activity.testId) {
        previous.testId = activity.testId;
      }
      if (previous.startTime == null && activity.startTime != null) {
        previous.startTime = activity.startTime;
      }
      this.logLiveSignalWrite(source, previous, activity, "rejected-rewind");
      return;
    }

    this.testActivity = activity;
    this.logLiveSignalWrite(source, previous, activity, "applied");
  }

  // Breadcrumb for every live-signal write that changes something, or that
  // we refused. The bug this exists for is only ever seen in a session
  // that is already hours old and cannot be reproduced on demand, so the
  // console needs to name the writer at the moment it happens.
  //
  // Quiet by design — only phase CHANGES are logged. Without that the 30s
  // resync would print a line a minute for the whole run, and the dashboard
  // preload a line per webapp per login (it clears the signal for every app
  // with no run in flight, twice, once per cacheThenRefresh pass). A log
  // nobody can skim is a log nobody reads.
  private logLiveSignalWrite(
    source: LiveSignalSource,
    previous: TestActivity | null,
    incoming: TestActivity | null,
    outcome: "applied" | "cleared" | "rejected-rewind",
  ): void {
    const previousPhase = previous ? phaseOf(previous.testStatus) : "none";
    const incomingPhase = incoming ? phaseOf(incoming.testStatus) : "none";
    if (previousPhase === incomingPhase) return;

    console.debug(
      `[liveSignal] webapp=${this.id} ${previousPhase} → ${incomingPhase} ` +
        `(${outcome}, source=${source}, testId=${incoming?.testId ?? previous?.testId ?? "none"})`,
    );
  }

  // Records the button-click timestamp used by the optimiztic-timing
  // logic. The running signal itself now lives in testActivity (set by
  // startTest / stopTest / the subscription handler), so this only
  // touches the timestamp.
  updateWebAppTestStatus(buttonClickTime: Date): void {
    this.lastStartButtonClickTime = buttonClickTime;
  }

  // Get Last Current Test ID
  // -------------------------
  // Returns the last current test id
  // The current test id may not be the one we want if the test is in progress
  //
  getLastCurrentTestId(): string | null {
    // Get the most recent 2 test ids
    var lastTestId = this.tests.length > 0 ? this.tests[0].id : null;
    var secondLastTestId = this.tests.length > 1 ? this.tests[1].id : null;

    if (lastTestId) {
      // If the end time is set then this is the ID we want
      if (this.tests[0].endTime && EMPTY_STRING !== this.tests[0].endTime) {
        return lastTestId;
      }
    }

    // If we are here then the most recent test is in progress, and the one we want is the test prior to this
    // If the test does not exist we are returning null, which is ok. Otherwise we are returning the id
    return secondLastTestId;
  }

  // Subscribe To Live Updates
  // --------------------------
  // Wires up the four custom AppSync subscriptions that keep this webapp's
  // local state in sync with backend writes:
  //   - onTestStatusChange       — TestResults row updated (status, endTime, etc.)
  //   - onWebAppTestStatusChange — per-webapp aggregate recomputed
  //   - onVulnStatusChange       — vuln status / risk / dateFixed changed
  //   - onWebApplicationChange   — webapp record itself updated
  // Each handler mutates this instance (or the matching nested model) and
  // emits an eventBus event so any rendered page can re-paint.
  //
  // Subscriptions are server-side filtered by webappid, so events for other
  // webapps in the same tenant don't reach this handler.
  //
  // Idempotent — calling twice doesn't double-subscribe.
  //
  async subscribeToLiveUpdates(subscriptionClient: any): Promise<void> {
    if (!subscriptionClient || !this.id) return;

    // In-flight resync FIRST, before any of the awaits below. It is the
    // fallback for missed subscription events, so hanging it off the end of
    // the subscribe sequence made it die with exactly the thing it exists to
    // cover: one subscribe that rejects (or never settles) and the webapp is
    // left with neither live events nor the poll that would have caught up.
    // See INFLIGHT_RESYNC_INTERVAL_MS for the cadence rationale.
    this.startInflightResyncWatchers();

    // A disposer belonging to a client that has been replaced (a fresh
    // login builds a new one) can't detach anything useful — drop it and
    // re-register on the client the subscriptions actually run over.
    if (this.liveUpdatesClient !== subscriptionClient) {
      this.liveUpdatesReconnectDisposer?.();
      this.liveUpdatesReconnectDisposer = null;
      this.liveUpdatesClient = subscriptionClient;
    }

    // Reconnecting restores the subscriptions but not the events missed
    // while the socket was down — AppSync has no replay. Resync on every
    // reconnect so status and findings catch up immediately rather than
    // waiting up to INFLIGHT_RESYNC_INTERVAL_MS.
    if (!this.liveUpdatesReconnectDisposer) {
      this.liveUpdatesReconnectDisposer =
        subscriptionClient.onReconnected?.(() => {
          this.resyncInflightTest().catch(() => {
            // Swallowed for the same reason as the periodic tick below.
          });
        }) ?? null;
    }

    if (!this.subscriptionToTestStatus) {
      this.subscriptionToTestStatus = await subscribeToTestStatusChanges({
        subscriptionClient,
        webappid: this.id,
        onNext: (msg: any) => this.handleTestStatusEvent(msg?.onTestStatusChange),
        onError: (err: any) =>
          console.error(`onTestStatusChange error (webapp ${this.id}):`, err),
      });
    }

    if (!this.subscriptionToWebAppTestStatus) {
      this.subscriptionToWebAppTestStatus = await subscribeToWebAppTestStatusChanges({
        subscriptionClient,
        webappid: this.id,
        onNext: (msg: any) =>
          this.handleWebAppTestStatusEvent(msg?.onWebAppTestStatusChange),
        onError: (err: any) =>
          console.error(`onWebAppTestStatusChange error (webapp ${this.id}):`, err),
      });
    }

    if (!this.subscriptionToVulnStatus) {
      this.subscriptionToVulnStatus = await subscribeToVulnStatusChanges({
        subscriptionClient,
        webappid: this.id,
        onNext: (msg: any) => this.handleVulnStatusEvent(msg?.onVulnStatusChange),
        onError: (err: any) =>
          console.error(`onVulnStatusChange error (webapp ${this.id}):`, err),
      });
    }

    if (!this.subscriptionToWebappUpdates) {
      this.subscriptionToWebappUpdates = await subscribeToWebApplicationChanges({
        subscriptionClient,
        webappid: this.id,
        onNext: (msg: any) =>
          this.handleWebApplicationChangeEvent(msg?.onWebApplicationChange),
        onError: (err: any) =>
          console.error(`onWebApplicationChange error (webapp ${this.id}):`, err),
      });
    }
  }

  // Unsubscribe From Live Updates
  // ------------------------------
  // Tears down all four live-update subscriptions. Called on logout and
  // when this webapp is removed from the local list. Safe to call when
  // already unsubscribed.
  unsubscribeFromLiveUpdates(): void {
    this.subscriptionToTestStatus?.unsubscribe?.();
    this.subscriptionToWebAppTestStatus?.unsubscribe?.();
    this.subscriptionToVulnStatus?.unsubscribe?.();
    this.subscriptionToWebappUpdates?.unsubscribe?.();
    this.subscriptionToTestStatus = null;
    this.subscriptionToWebAppTestStatus = null;
    this.subscriptionToVulnStatus = null;
    this.subscriptionToWebappUpdates = null;
    this.liveUpdatesReconnectDisposer?.();
    this.liveUpdatesReconnectDisposer = null;
    this.liveUpdatesClient = null;
    this.stopInflightResyncWatchers();
  }

  // In-flight resync — periodic poll + visibility-change refetch
  // -------------------------------------------------------------
  // Belt-and-braces for the AppSync subscription: if
  // onTestStatusChange silently drops (WebSocket idle timeout,
  // transient network failure), the UI would otherwise stay pinned
  // to whatever status it last saw — typically "Starting…" from the
  // optimiztic placeholder on the click that kicked the test off.
  //
  // resyncInflightTest fetches the current test snapshot via the
  // slim getTestStatusSnapshot query (no vulns) and feeds the
  // result straight through handleTestStatusEvent — same write-path
  // as a live event, so the local model converges regardless of
  // whether the update arrived via subscription or via this
  // fallback.
  //
  // The cadence is intentionally per-WebSocket-style cheap: only
  // fires while a test is in flight on THIS webapp (testRunning &&
  // tests[0]); shuts off once the latest test reaches terminal.

  private startInflightResyncWatchers(): void {
    if (this.inflightResyncInterval === null) {
      this.inflightResyncInterval = setInterval(() => {
        this.resyncInflightTest().catch(() => {
          // Swallow — same belt-and-braces principle: a failed
          // resync should never break the page, and the next tick
          // or the next subscription event will catch up.
        });
      }, INFLIGHT_RESYNC_INTERVAL_MS);
    }

    if (this.inflightResyncVisibilityHandler === null) {
      this.inflightResyncVisibilityHandler = () => {
        if (document.visibilityState === "visible") {
          this.resyncInflightTest().catch(() => {
            // Same swallow rationale as above.
          });
        }
      };
      document.addEventListener(
        "visibilitychange",
        this.inflightResyncVisibilityHandler,
      );
    }
  }

  private stopInflightResyncWatchers(): void {
    if (this.inflightResyncInterval !== null) {
      clearInterval(this.inflightResyncInterval);
      this.inflightResyncInterval = null;
    }
    if (this.inflightResyncVisibilityHandler !== null) {
      document.removeEventListener(
        "visibilitychange",
        this.inflightResyncVisibilityHandler,
      );
      this.inflightResyncVisibilityHandler = null;
    }
  }

  // Resync everything that can go stale while a run is in flight. Status
  // and findings are resynced independently: each has its own failure
  // modes and its own staleness guards, and a skip on one must never
  // suppress the other.
  private async resyncInflightTest(): Promise<void> {
    if (!deriveLiveTestState(this).isRunning) return;

    const targetTestId = this.resolveInflightTestId();
    if (!targetTestId) {
      // No test row to poll — the live signal carries no testid and the
      // model holds no in-flight row to guess from (the optimiztic window
      // between the click and the backend's testid, or a preload that
      // shipped only the latest terminal). This used to return here, which
      // made every subsequent tick a silent no-op: whatever phase the UI
      // was showing became permanent for the rest of the run. Fall back to
      // the endTime-authoritative reconcile — it cannot refine the phase
      // (its answer is a bare boolean) but it does clear a signal for a run
      // that has ended, and it is one small query per tick in a window
      // that normally lasts under a second.
      await this.isTestRunning().catch(() => {
        // Same swallow rationale as the caller's: a failed reconcile must
        // never break the page, and the next tick retries.
      });
      return;
    }

    await Promise.allSettled([
      this.resyncInflightTestStatus(targetTestId),
      this.resyncInflightFindings(targetTestId),
    ]);
  }

  // Which test is the live signal about? Shared by both resync halves.
  //
  // Resync the test the live signal actually NAMES, not tests[0].
  // tests[0] is "the in-flight test" only when the test list is both
  // complete and newest-first. When it isn't — a truncated list, a
  // preload that shipped only the latest terminal — tests[0] can be an
  // old Completed test, and polling it would feed that terminal status
  // into handleTestStatusEvent and clear the live signal for a run
  // that is still going. That is exactly how a live pentest ended up
  // behind a "Start Pentest" button: the next click issued startTest
  // and the backend answered "Test already running".
  //
  // Falling back to the test list is still right when the activity has no
  // id yet (the transitional windows documented on TestActivity), but only
  // to a row that could plausibly BE the live run. tests[0] is not that row
  // whenever the list is newest-first-but-terminal — which the dashboard
  // preload guarantees, since it replaces the whole list with the latest
  // terminal test. Polling that row forever answers a question nobody
  // asked: its status never changes, so the live phase is never refined,
  // and the identity guard in handleTestStatusEvent (correctly) refuses to
  // let its terminal status clear the signal. The result is a UI frozen on
  // whatever phase it happened to be showing.
  private resolveInflightTestId(): string | null {
    const trackedTestId = this.testActivity?.testId;
    if (trackedTestId) {
      return this.isPollableTestId(trackedTestId) ? trackedTestId : null;
    }

    // Newest non-terminal row — the only kind that can still be the run in
    // flight. this.tests is held newest-first (backfillTestHistory sorts it
    // and every insert unshifts), so the first match is the newest.
    const inflightTest = this.tests.find(
      (test) =>
        this.isPollableTestId(test.id) &&
        (!test.testStatus || !TERMINAL_TEST_STATUSES.has(test.testStatus)),
    );
    return inflightTest?.id ?? null;
  }

  // Optimiztic placeholders carry a locally-generated id ("pending-…"),
  // not a backend one, so reads against them would 404. Once startTest
  // swaps in the real id the resync picks this up on the next tick.
  private isPollableTestId(testId?: string | null): boolean {
    return !!testId && !testId.startsWith(OPTIMIZTIC_TEST_ID_PREFIX);
  }

  // Findings half of the in-flight resync — the safety net for the
  // onVulnerabilityDiscovered stream.
  //
  // Two distinct failures are covered. First, a stream that is open but
  // missed events: nothing replays them, so the reconcile reads them out
  // of the backend. Second, a stream that a consumer asked for and then
  // lost entirely — a socket that died while the laptop slept, or a
  // subscription AppSync terminated on token expiry — which is re-opened.
  //
  // Note this runs for any webapp with a run in flight, not only the one
  // being viewed: the reconcile is a slim read, and having it already
  // done means the table is right the moment the user navigates in.
  private async resyncInflightFindings(targetTestId: string): Promise<void> {
    const liveTest = this.tests.find((test) => test.id === targetTestId);
    if (!liveTest?.isLive) return;

    if (liveTest.needsVulnStreamReopen() && this.liveUpdatesClient) {
      // Re-opening runs its own catch-up, so reconciling again here
      // would just be a second read of the same rows.
      await liveTest.subscribeToVulns(this.liveUpdatesClient, this.id);
      return;
    }

    await liveTest.reconcileLiveFindings(this.id);
  }

  // Status half of the in-flight resync — fetch the current snapshot and
  // feed it through the same handler a live event would take.
  private async resyncInflightTestStatus(targetTestId: string): Promise<void> {
    const snapshot = await fetchTestStatusSnapshot(targetTestId);
    if (!snapshot) return;

    // The tracked run may not be in this.tests at all (the preload ships
    // only the latest terminal). Rank against it when we have it;
    // otherwise there is no local state to regress and the snapshot is
    // adopted — handleTestStatusEvent self-heals the missing instance.
    const liveTest = this.tests.find((test) => test.id === targetTestId);
    if (!liveTest) {
      this.handleTestStatusEvent(snapshot);
      return;
    }

    // Monotonic guard. If the subscription has already moved us
    // forward (Provisioning → Running, say) and this snapshot was
    // computed against a behind-the-write read replica, applying
    // it would briefly rewind the UI (Running → Provisioning) until
    // the next subscription event corrected us. The user sees the
    // Pentest button flip Stop → Starting → Stop and reasonably
    // assumes the FE is broken. Drop the resync update when it
    // would regress; the next tick or the next subscription event
    // catches up.
    //
    // Ranked against BOTH the test row and the live signal, because the
    // two legitimately diverge: isTestRunning()'s coarse seed advances the
    // signal without touching any row, and the dashboard preload writes a
    // phase with no testid to attach it to. Ranking against the row alone
    // let a stale snapshot through whenever the signal was the fresher of
    // the two. setTestActivity's own monotonic rule now backstops the
    // button, but the row feeds isLive and the status pill, so it wants
    // the same protection.
    const trackedActivity = this.testActivity;
    const trackedRank =
      trackedActivity && trackedActivity.testId === targetTestId
        ? testStatusRank(trackedActivity.testStatus)
        : 0;
    const localRank = Math.max(testStatusRank(liveTest.testStatus), trackedRank);
    const remoteRank = testStatusRank(snapshot.testStatus);
    if (remoteRank < localRank) return;

    this.handleTestStatusEvent(snapshot);
  }

  // Is this test the run that testActivity is currently tracking?
  //
  // Identity is by testId whenever we know it. In the transitional
  // windows where the activity carries no id — the dashboard preload
  // (which ships inFlightTestStatus without the id), the optimiztic
  // pre-backend window in startTest, and the endTime-authoritative
  // reconcile in isTestRunning — we fall back to comparing startTime,
  // which the preload does supply.
  //
  // When neither can confirm identity we answer false, i.e. "don't
  // clear". The two failure modes are not symmetric: a stale "Testing"
  // pill is self-correcting (the stop path's isTestRunning() check is
  // authoritative, and the next matching event settles it), whereas
  // wrongly clearing the signal hides a running pentest AND removes the
  // user's ability to stop it.
  private isTrackedRun(test: TestResultsClass): boolean {
    const activity = this.testActivity;
    if (!activity) return true; // nothing tracked — clearing is a no-op
    if (activity.testId) return activity.testId === test.id;
    if (activity.startTime != null && test.startTime) {
      return Number(test.startTime) === activity.startTime;
    }
    return false;
  }

  // Test status update — find the affected test in this.tests and update its
  // mutable fields in place. If endTime newly transitions to set, flip
  // testRunning false. Emits TEST_STATUS_UPDATE with the testid.
  //
  // Self-heals when the test isn't already known. The dashboard preload
  // only carries the most recent test, so a test started via the schedule,
  // a different session, or any path that bypassed webapp.startTest can
  // surface here as an unknown id. Rather than silently dropping the
  // event (which leaves the UI stuck on "Test in progress"), we register
  // a slim TestResultsClass for it and proceed with the normal update
  // path.
  private handleTestStatusEvent(payload: any): void {
    if (!payload?.id) return;

    let matchingTest = this.tests.find((test) => test.id === payload.id);
    if (!matchingTest) {
      const newTest = new TestResultsClass();
      newTest.id = payload.id;
      newTest.owner = payload.owner ?? EMPTY_STRING;
      newTest.startTime = payload.startTime ?? EMPTY_STRING;
      newTest.startTrigger = payload.startTrigger ?? EMPTY_STRING;
      this.tests.unshift(newTest);
      matchingTest = newTest;
    }

    if (matchingTest) {
      // Apply every field from the payload directly. testStatus is
      // assigned without a fallback because the backend now publishes
      // explicit values for every transition (Provisioning → Running →
      // Stopping → terminal); falling back to the previous status would
      // re-introduce the kind of "previous-state" coupling we want to
      // avoid. Same reasoning for the retest fields, which are nulled
      // explicitly by the backend on reclassification.
      matchingTest.endTime = payload.endTime ?? matchingTest.endTime;
      matchingTest.endTrigger = payload.endTrigger ?? matchingTest.endTrigger;
      if (payload.testStatus !== undefined) {
        matchingTest.testStatus = payload.testStatus;
      }
      matchingTest.testStatusMessage =
        payload.testStatusMessage ?? matchingTest.testStatusMessage;
      matchingTest.statistics = payload.statistics ?? matchingTest.statistics;
      matchingTest.retestExpiresAt = payload.retestExpiresAt ?? null;
      matchingTest.isRetestOf = payload.isRetestOf ?? null;
      matchingTest.originalTestDate = payload.originalTestDate ?? null;
      matchingTest.isLive = isTestLive(matchingTest.testStatus, matchingTest.endTime);

      // The live signal is a pure function of the latest status. No
      // previous-state branching, no "first endTime arrival" tricks —
      // just a direct read of the status the backend told us about,
      // funneled through the single setter.
      // - Provisioning / Running / Stopping → set the in-flight activity.
      // - Completed / Invalid / Failed → clear it (setTestActivity(null)).
      // - null (initial) → leave the activity unchanged so an event
      //   without a status doesn't accidentally flip a running test off.
      if (matchingTest.testStatus) {
        const isTerminal = TERMINAL_TEST_STATUSES.has(matchingTest.testStatus);
        // A terminal status clears the live signal ONLY when the event is
        // about the run that signal is tracking. A terminal status for
        // some other (older) test says nothing about whether the tracked
        // run is still going, and adopting it strands a live pentest
        // behind a "Start Pentest" button. Non-terminal events are offered
        // unconditionally — they can only turn the signal ON or move it
        // forward, and that is what self-heals a run started in another
        // session or by the scheduler. Which of them actually land is
        // setTestActivity's call: it drops one that would rewind the run
        // it is already tracking.
        if (isTerminal) {
          if (this.isTrackedRun(matchingTest)) {
            this.setTestActivity(null, {
              source: LIVE_SIGNAL_SOURCE.statusEventTerminal,
            });
          }
        } else {
          this.setTestActivity(
            {
              testId: matchingTest.id,
              testStatus: matchingTest.testStatus,
              startTime:
                matchingTest.startTime != null && matchingTest.startTime !== ""
                  ? Number(matchingTest.startTime)
                  : null,
            },
            { source: LIVE_SIGNAL_SOURCE.statusEvent },
          );
        }
        if (isTerminal || matchingTest.testStatus === TEST_STATUS_STOPPING) {
          // Test logic has wrapped up — no more vulns expected.
          matchingTest.unsubscribeFromVulns?.();
        }
      }

    }

    eventBus.emit(TEST_STATUS_UPDATE, {
      webappid: this.id,
      testid: payload.id,
    });
  }

  // WebApp test status update — repopulate the aggregate. populateFromData
  // mirrors the bulk-load path used at login.
  private handleWebAppTestStatusEvent(payload: any): void {
    if (!payload) return;
    this.webAppTestStatus.populateFromData(payload);
    eventBus.emit(WEBAPP_TEST_STATUS_UPDATE, { webappid: this.id });
  }

  // Vuln status update — locate the affected vuln across this webapp's
  // tests and merge the slim payload into the in-memory instance. The
  // new-vuln path goes through onNewVulnerability (on the test instance),
  // not here.
  //
  // Partial merge (not setVulnerability) is intentional: the
  // onVulnStatusChange payload deliberately omits `render` (base64
  // screenshot) and `videoURL` to keep the WebSocket frame under AppSync's
  // ~240 KB cap. Calling setVulnerability with the slim payload would
  // erase those fields from the existing model. Instead, only overlay the
  // fields the subscription is responsible for surfacing.
  private handleVulnStatusEvent(payload: any): void {
    if (!payload?.id) return;

    for (const test of this.tests) {
      const matchingVuln = test.vulns?.find((vuln) => vuln.id === payload.id);
      if (matchingVuln) {
        if (payload.dateFirstFound !== undefined)
          matchingVuln.dateFirstFound = payload.dateFirstFound;
        if (payload.dateFixed !== undefined) matchingVuln.dateFixed = payload.dateFixed;
        if (payload.dateLastSeen !== undefined)
          matchingVuln.dateLastSeen = payload.dateLastSeen;
        if (payload.reference !== undefined) matchingVuln.reference = payload.reference;
        if (payload.owner !== undefined) matchingVuln.owner = payload.owner;
        if (payload.pageUrl !== undefined) matchingVuln.pageUrl = payload.pageUrl;
        if (payload.vulnLocation !== undefined)
          matchingVuln.vulnLocation = payload.vulnLocation;
        if (payload.title !== undefined) matchingVuln.title = payload.title || "";
        if (Array.isArray(payload.additionalRenders))
          matchingVuln.additionalRenders = payload.additionalRenders
            .filter((r: { url?: string }) => r && r.url)
            .map((r: { label?: string; url: string }) => ({
              label: r.label || "",
              url: r.url,
            }));
        if (payload.details !== undefined) matchingVuln.details = payload.details || [];
        if (payload.riskRating !== undefined) matchingVuln.riskRating = payload.riskRating;
        if (payload.vulnCategory !== undefined)
          matchingVuln.vulnCategory = formatVulnCategory(payload.vulnCategory);
        if (payload.vulnName !== undefined)
          matchingVuln.vulnName = convertFromGraphAPItoUI(payload.vulnName);
        if (payload.vulnStatus !== undefined)
          matchingVuln.vulnStatus = convertFromGraphAPItoUI(payload.vulnStatus);
        if (payload.stepsToReproduce !== undefined)
          matchingVuln.stepsToReproduce = payload.stepsToReproduce || [];
        if (payload.recommendations !== undefined)
          matchingVuln.recommendations = payload.recommendations || [];
        if (typeof payload.requiresAuthn === "boolean")
          matchingVuln.requiresAuthentication = payload.requiresAuthn;
        break;
      }
    }

    eventBus.emit(VULN_STATUS_UPDATE, {
      webappid: this.id,
      vulnid: payload.id,
    });
  }

  // WebApp record update — overlay incoming fields onto this instance.
  // Uses Object.assign for a partial merge so fields the backend didn't
  // include (e.g. transient ones like webAppTestStatus) aren't clobbered.
  private handleWebApplicationChangeEvent(payload: any): void {
    if (!payload?.id || payload.id !== this.id) return;

    if (payload.name !== undefined) this.name = payload.name;
    if (payload.url !== undefined) this.url = payload.url;
    if (payload.render !== undefined) this.render = payload.render;
    if (payload.owner !== undefined) this.owner = payload.owner;
    if (payload.verificationString !== undefined)
      this.verificationString = payload.verificationString;
    if (payload.ownershipVerified !== undefined)
      this.ownershipVerified = payload.ownershipVerified;
    if (payload.allowedLinks !== undefined) this.allowedLinks = payload.allowedLinks;
    if (payload.deniedLinks !== undefined) this.deniedLinks = payload.deniedLinks;
    if (payload.apiDomains !== undefined) this.apiDomains = payload.apiDomains;
    if (payload.maxRequestsPerSecond !== undefined)
      this.maxRequestsPerSecond = payload.maxRequestsPerSecond;
    if (payload.featureTeamName !== undefined)
      this.featureTeamName = payload.featureTeamName;
    if (payload.frontEndFramework !== undefined)
      this.frontEndFramework = payload.frontEndFramework;
    if (payload.backEndFramework !== undefined)
      this.backEndFramework = payload.backEndFramework;
    if (payload.dataType !== undefined) this.dataType = payload.dataType;
    if (payload.userType !== undefined) this.userType = payload.userType;
    if (payload.dateToTest !== undefined) this.dateToTest = payload.dateToTest;
    if (payload.testFrequency !== undefined)
      this.testFrequency = payload.testFrequency;
    if (payload.dateAdded !== undefined) this.dateAdded = payload.dateAdded;
    if (payload.abbrev !== undefined) this.abbrev = payload.abbrev;
    if (payload.color !== undefined) this.color = payload.color;

    eventBus.emit(WEBAPP_UPDATE, { webappid: this.id });
  }

  // Reattach to a running test on login / page mount.
  //
  // Historically this also subscribed to onNewVulnerability so live
  // findings could stream into the model during the run. That live-
  // streaming surface was retired in favour of refetching the full
  // test data at terminal-status transitions — the user doesn't
  // expect to watch findings appear, and the previous design pulled
  // in race-prone merge logic that caused stale duplicates to
  // accumulate.
  //
  // The function is preserved as a hook for future state-restoration
  // needs (e.g. catching up on missed onTestStatusChange events) but
  // is currently a no-op aside from a status fetch. Kept in the API
  // so callers (loadFullData) don't have to learn a new shape.
  //
  // subscriptionClient is intentionally unused — accepted so the
  // public signature stays stable for external callers.
  async subscribeToRunningTest(_subscriptionClient: any): Promise<void> {
    await this.isTestRunning();
  }


  // Is Test Running
  // ----------------
  // The endTime-authoritative reconcile: asks the backend "does the
  // latest test row for this webapp have no endTime?". Used on login /
  // page mount / after a rejected start. Reconciles the single live
  // signal (testActivity) from that answer.
  //
  // Two subtleties the reconcile must respect:
  //  - The BE query returns a BARE boolean, not the phase. So when it
  //    says "running" but we have no richer known activity, we record a
  //    coarse Running placeholder; the next subscription/resync refines
  //    it to the real phase (and real testid).
  //  - Stopping legitimately has an endTime set, so the BE query returns
  //    false for it — but the FE treats Stopping as still "in flight"
  //    (the environment is still tearing down). So a "not running" answer
  //    must NOT clear a known Stopping activity; the terminal subscription
  //    event clears it instead.
  async isTestRunning(): Promise<{ isRunning: boolean }> {
    // Make the graphql call to get the status. The backend field is a Boolean:
    // true when a test is in flight for this web app, false otherwise. Compare
    // to `true` explicitly so a null/undefined (e.g. field absent) reads as
    // "not running" rather than throwing.
    const response = await API.graphql(
      graphqlOperation(getIsWebApplicationTestRunning, { webappid: this.id })
    );
    const beReportsRunning =
      getGraphData(response, "getIsWebApplicationTestRunning") === true;

    if (beReportsRunning) {
      // Preserve a richer known phase (Provisioning / Running / Stopping)
      // if we already have one; otherwise seed a coarse Running placeholder.
      // Flagged as a placeholder: Running here is this function's own
      // invention (the backend answered a bare boolean), so the next real
      // phase must be free to replace it — including with Provisioning,
      // which the monotonic rule would otherwise read as a rewind. Getting
      // this wrong would pin the UI to a Stop Pentest button the backend
      // rejects for the whole provisioning window.
      if (!this.testActivity) {
        this.setTestActivity(
          {
            testId: null,
            testStatus: TEST_STATUS_RUNNING,
            startTime: null,
            phaseIsPlaceholder: true,
          },
          { source: LIVE_SIGNAL_SOURCE.isTestRunningCoarse },
        );
      }
    } else if (this.testActivity?.testStatus !== TEST_STATUS_STOPPING) {
      // Not running per endTime, and we're not mid-teardown → clear.
      this.setTestActivity(null, {
        source: LIVE_SIGNAL_SOURCE.isTestRunningEnded,
      });
    }

    // Return the RAW endTime-authoritative answer, not the derived live
    // state. The stop-safety caller (WebApplication.executeTestAction)
    // uses it to decide whether issuing a stop would be wasted — it needs
    // "does a Provisioning/Running row exist?" (endTime null), which is
    // exactly this boolean. The Stopping-preservation above only shapes
    // the UI signal (testActivity), not this decision value.
    return { isRunning: beReportsRunning };
  }
}

export default WebApplicationClass;
